import {
  IconArrowLeft,
  IconCloudUpload,
  IconRosetteDiscountCheckFilled,
} from "@tabler/icons-react";
import { useNavigate } from "react-router";
import Button from "../../components/Button";
import { useForm } from "react-hook-form";
import { connect, useDispatch } from "react-redux";
import { importPendingContract } from "../../store/pending-contract/actions";
import { useEffect, useState } from "react";
import { isScopeLoading } from "../../store/scope-loading/helpers";
import { RootState } from "../../store/root-reducer";
import { PendingContractScope } from "../../store/pending-contract/types";
import sampleExcel from "../../assets/protectall_sample_contracts.xlsx";
import sampleFileLayout from "../../assets/dealer_contract_file_layout.xlsx";
import { TbDownload } from "react-icons/tb";
import FileViewComponent from "../../components/FileViewComponent";
import toast from "react-hot-toast";
import readXlsxFile from "read-excel-file";
import NotAuthorizedPage from "../../components/NotAuthorizedPage";
import SelectField from "../../components/SelectField";

interface AssociatedDealer {
  id: string;
  alias: string;
}

function AddContractFile({
  loading,
  isAuthorized,
  associated_dealers,
}: {
  loading: boolean;
  isAuthorized: boolean;
  associated_dealers: AssociatedDealer[];
}) {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [uploadedFiles, setUploadedFiles] = useState<File>();
  const [fileViewOpen, setFileViewOpen] = useState(false);

  const handleFileUpload = (files: FileList | File[]) => {
    if (!files || files.length === 0) return;
    const baseFile = files[0];
    if (
      baseFile.type !==
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" &&
      baseFile.type !== "application/vnd.ms-excel"
    ) {
      toast.error("Please upload a valid file(Excel)", {
        position: "bottom-center",
      });
      return false;
    }
    readXlsxFile(baseFile)
      .then(() => {
        setUploadedFiles(baseFile);
        setFileViewOpen(true);
      })
      .catch(() => {
        toast.error("Please upload a valid file(Excel)", {
          position: "bottom-center",
        });
        return false;
      });
  };

  const [successModal, setSuccessModal] = useState({
    show: false,
    numberOfRecordImported: 0,
    notImportedRecords: "",
  });
  const formMethods = useForm({
    defaultValues: {
      dealer_id: associated_dealers[0]?.id || "",
    },
  });
  const { handleSubmit, control, setValue } = formMethods;

  const handleBeforeUnload = (event) => {
    event.preventDefault();
    event.returnValue = true;
  };

  // Ensure dealer_id is set for single dealer accounts
  useEffect(() => {
    if (associated_dealers.length === 1) {
      setValue("dealer_id", associated_dealers[0]?.id);
    }
  }, [associated_dealers, setValue]);

  const onSubmit = (values) => {
    if (!values?.excel_file?.length) return;
    window.addEventListener("beforeunload", handleBeforeUnload);
    if (values?.excel_file?.length > 0)
      dispatch(
        importPendingContract(
          values.excel_file[0],
          values.dealer_id?.value || values?.dealer_id,
          (numberOfRecordImported = 0, notImportedRecords = "") =>
            setSuccessModal({
              show: true,
              numberOfRecordImported,
              notImportedRecords:
                notImportedRecords?.toString()?.replace(",", ", ") || "",
            })
        )
      );
  };

  const processUploadedFile = () => {
    const file = uploadedFiles;
    if (!file) return;

    window.addEventListener("beforeunload", handleBeforeUnload);
    const { dealer_id } = formMethods.getValues();

    dispatch(
      importPendingContract(
        file,
        dealer_id?.["value"] || dealer_id,
        (numberOfRecordImported = 0, notImportedRecords = "") =>
          setSuccessModal({
            show: true,
            numberOfRecordImported,
            notImportedRecords:
              notImportedRecords?.toString()?.replace(",", ", ") || "",
          })
      )
    );
  };

  useEffect(() => {
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, []);

  if (!isAuthorized) {
    return <NotAuthorizedPage />;
  }

  return (
    <div className="container flex flex-col h-[calc(100dvh-64px)]">
      <div className="sm:pt-9 pt-6 pb-6">
        <button
          className="flex gap-3 text-gray-700 items-center font-medium"
          onClick={() => navigate("/contracts")}
        >
          <IconArrowLeft />
          <p>Add Contract File</p>
        </button>
      </div>

      {successModal.show ? (
        <div className="flex items-center justify-center h-full">
          <div className="bg-white border border-gray-200 rounded-3xl overflow-hidden p-9 max-w-[430px] w-full mx-auto space-y-6 mb-10">
            <h2 className="text-2xl text-gray-800 font-semibold text-center">
              Contracts Import Successfully
            </h2>
            <IconRosetteDiscountCheckFilled
              size={80}
              className="text-green-600 mx-auto"
            />
            <p className="text-gray-600 text-center">
              {successModal.numberOfRecordImported} Contracts Imported
            </p>
            {successModal.notImportedRecords && (
              <div className="space-y-2">
                <p className="text-gray-600 text-center">
                  Not Imported Contracts Invoice numbers:
                </p>
                <p className="text-gray-600 text-center ">
                  {successModal.notImportedRecords}
                </p>
                <p className="text-gray-600 text-center text-sm">
                  Please create it manually or fix the data and try to
                  re-upload.
                </p>
              </div>
            )}
            <Button
              variant="blue"
              btnClassName="w-full"
              onClick={() => navigate(`/contracts`)}
            >
              Return To Contract Dashboard
            </Button>
          </div>
        </div>
      ) : (
        <div className="flex items-center justify-center h-full">
          {fileViewOpen ? (
            <div className="w-full h-full">
              <div className="bg-white border border-gray-200 rounded-2xl p-5  space-y-3 transition-all">
                <FileViewComponent
                  files={uploadedFiles}
                  onBack={() => {
                    setFileViewOpen(false);
                    setUploadedFiles(null);
                  }}
                  onProcessFile={processUploadedFile}
                  loading={loading}
                />
              </div>
            </div>
          ) : (
            <form onSubmit={handleSubmit(onSubmit)} className="w-full max-w-xl">
              <div className="bg-white border border-gray-200 rounded-2xl p-10  space-y-3 transition-all">
                <h2 className="text-3xl text-gray-800 font-bold text-center leading-tight">
                  Upload Contracts File
                </h2>
                <div className="bg-red-100 border-l-4 border-red-400 text-red-800 text-sm p-4 rounded-md shadow-sm">
                  <span className="font-semibold">IMPORTANT:</span> Each
                  contract must have a unique invoice number for the system. So
                  if you have multiple contracts on a single invoice, please add
                  a “-1”, “-2”, etc to each row of the spreadsheet with a unique
                  contract. Thank you!
                </div>

                {associated_dealers.length > 1 && (
                  <div className="space-y-1">
                    <SelectField
                      control={control as any}
                      name="dealer_id"
                      label="Select Associated Dealer"
                      placeholder="Select Associated Dealer"
                      options={associated_dealers.map((d) => ({
                        label: d.alias,
                        value: d.id,
                      }))}
                      defaultValue={{
                        label: associated_dealers[0].alias,
                        value: associated_dealers[0].id,
                      }}
                      required
                    />
                  </div>
                )}

                <div className="space-y-2">
                  <div
                    className={`w-full flex-col flex justify-center items-center relative sm:p-5 h-[150px] !bg-gray-100 border border-dashed border-gray-300 p-4 rounded-md hover:bg-gray-200 transition`}
                  >
                    <input
                      type="file"
                      name="excel_file"
                      accept="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel"
                      className="absolute inset-0 text-transparent opacity-0 cursor-pointer"
                      multiple={false}
                      onChange={(e) => {
                        handleFileUpload(e.target.files);
                      }}
                    />
                    <div className="mb-1">
                      <IconCloudUpload
                        stroke={1}
                        className="text-gray-500"
                        size={40}
                      />
                    </div>
                    <span className="text-gray-500 text-sm text-center">
                      Click or Drag Files to Upload
                    </span>
                  </div>
                </div>
                <div className="flex flex-col sm:flex-row sm:justify-between gap-2">
                  <a
                    href={sampleExcel}
                    className="text-sm text-blue-600 hover:underline flex items-center gap-1"
                    download
                  >
                    <TbDownload className="size-4" />
                    Sample Contract File
                  </a>
                  <a
                    href={sampleFileLayout}
                    className="text-sm text-blue-600 hover:underline flex items-center gap-1"
                    download
                  >
                    <TbDownload className="size-4" />
                    File Specification Guide
                  </a>
                </div>
              </div>
            </form>
          )}
        </div>
      )}
    </div>
  );
}

const mapStateProps = (state: RootState) => {
  return {
    loading: isScopeLoading(state.scopeLoading, PendingContractScope),
    isAuthorized: state.user.selectedOrg.permissions.includes(
      "create:bulk-contract"
    ),
    associated_dealers: state.user.selectedOrg.associated_dealers || [],
  };
};

export default connect(mapStateProps)(AddContractFile);
